import React from "react";

import { formatDistanceStrict } from "date-fns";
import { noop } from "lodash";
import type { GraphQLResponse } from "relay-runtime/lib/network/RelayNetworkTypes";

import { generateAndSubmitLogoutForm } from "~/lib/auth";
import { parseDate } from "~/lib/utils/dates";
import { isDesktopApp } from "~/lib/utils/platform";
import type { ToastyOptions } from "~/ui/toast";
import toast, { ToastActionButton } from "~/ui/toast";

const REAUTH_TOAST_ID = "session-reauth-warning";
const REAUTH_REQUIRED_HEADER = "X-Fellow-Reauth-Required";
const REAUTH_DEADLINE_HEADER = "X-Fellow-Reauth-Deadline";
const SESSION_STATUS_PING_QUERY = "query SessionStatusPing { viewer { authenticatedAt } }";
const WARNING_DISPLAY_WINDOW_MS = 12 * 60 * 60 * 1000;
const WARN_TIER_THRESHOLD_MS = 30 * 60 * 1000;
const ERROR_TIER_THRESHOLD_MS = 3 * 60 * 1000;
const SECONDS_LABEL_THRESHOLD_MS = 90 * 1000;
let reauthWarningTimeout: number | undefined;
let reauthStatusCheckInFlight = false;

type ReauthToastType = "info" | "warn" | "error";

export function getLoginUrlForReauth(location: Location = window.location): string {
	const loginPageURL = new URL(isDesktopApp ? "/auth/desktop-splash/" : "/auth/login/", location.href);
	const next = location.pathname + location.search + location.hash;
	loginPageURL.search = new URLSearchParams({ failed: "1", next }).toString();
	return loginPageURL.href;
}

export function redirectToLoginForReauth(): Promise<GraphQLResponse> {
	const loginPageURL = new URL(getLoginUrlForReauth());
	if (loginPageURL.pathname !== window.location.pathname) {
		generateAndSubmitLogoutForm("/auth/logout/", loginPageURL.href);
	}
	return new Promise<GraphQLResponse>(noop);
}

function handleReauthActionClick(): void {
	redirectToLoginForReauth();
}

const reauthToastActionElement = React.createElement(
	ToastActionButton,
	{ onClick: handleReauthActionClick },
	"Re-authenticate now",
);

export function dismissReauthWarningToast(): void {
	if (reauthWarningTimeout === undefined && !toast.isActive(REAUTH_TOAST_ID)) {
		return;
	}
	if (reauthWarningTimeout !== undefined) {
		window.clearTimeout(reauthWarningTimeout);
		reauthWarningTimeout = undefined;
	}
	toast.dismiss(REAUTH_TOAST_ID);
}

function getRemainingMilliseconds(deadlineValue: string | null): number | null {
	const deadline = parseDate(deadlineValue);
	if (!deadline) {
		return null;
	}

	return Math.max(deadline.getTime() - Date.now(), 0);
}

function getReauthWarningType(remainingMilliseconds: number | null): ReauthToastType {
	if (remainingMilliseconds !== null && remainingMilliseconds < ERROR_TIER_THRESHOLD_MS) {
		return "error";
	}
	if (remainingMilliseconds !== null && remainingMilliseconds < WARN_TIER_THRESHOLD_MS) {
		return "warn";
	}
	return "info";
}

function formatRemainingTime(deadline: Date, remainingMilliseconds: number): string {
	if (remainingMilliseconds < SECONDS_LABEL_THRESHOLD_MS) {
		const seconds = Math.ceil(remainingMilliseconds / 1000);
		return `${seconds} second${seconds === 1 ? "" : "s"}`;
	}

	return formatDistanceStrict(deadline, new Date(), {
		roundingMethod: "round",
	});
}

// Schedule the next tick at the half-minute boundary for the rounded remaining time
// so the displayed label flips exactly when the user-visible value changes (e.g. "5 minutes"
// → "4 minutes"). Inside the seconds-label window we tick once a second instead.
function getMillisecondsUntilLabelChanges(remainingMilliseconds: number): number {
	if (remainingMilliseconds < SECONDS_LABEL_THRESHOLD_MS) {
		return 1000;
	}

	const roundedMinutes = Math.round(remainingMilliseconds / 60_000);
	const nextRoundedMinuteBoundary = (roundedMinutes - 0.5) * 60_000;
	const millisecondsUntilBoundary = remainingMilliseconds - nextRoundedMinuteBoundary;
	return Math.max(Math.floor(millisecondsUntilBoundary) + 1, 1);
}

function getReauthWarningMessage(deadlineValue: string | null): string {
	const deadline = parseDate(deadlineValue);
	const remainingMilliseconds = getRemainingMilliseconds(deadlineValue);
	const remainingTime =
		deadline && remainingMilliseconds !== null && remainingMilliseconds > 0
			? ` Sign in again within ${formatRemainingTime(deadline, remainingMilliseconds)} to keep working.`
			: "";

	return `Your workspace requires periodic re-authentication.${remainingTime}`;
}

function getReauthWarningToastOptions(): ToastyOptions {
	return {
		toastId: REAUTH_TOAST_ID,
		closeButton: false,
		autoClose: false,
		closeOnClick: false,
		draggable: false,
		actions: reauthToastActionElement,
	};
}

function updateReauthWarningToast(deadlineValue: string | null): void {
	const remainingMilliseconds = getRemainingMilliseconds(deadlineValue);
	toast.update(REAUTH_TOAST_ID, {
		...getReauthWarningToastOptions(),
		type: getReauthWarningType(remainingMilliseconds),
		render: getReauthWarningMessage(deadlineValue),
	});
}

function showNewReauthWarningToast(deadlineValue: string | null): void {
	const remainingMilliseconds = getRemainingMilliseconds(deadlineValue);
	const type = getReauthWarningType(remainingMilliseconds);
	const message = getReauthWarningMessage(deadlineValue);
	const options = getReauthWarningToastOptions();

	if (type === "error") {
		toast.error(message, options);
	} else if (type === "warn") {
		toast.warn(message, options);
	} else {
		toast.info(message, options);
	}
}

async function checkReauthStatus(): Promise<void> {
	if (reauthStatusCheckInFlight) {
		return;
	}

	reauthStatusCheckInFlight = true;
	try {
		// A no-op GraphQL query is the cheapest probe we have: the request hits the
		// reauth middleware (so we get the 403 + X-Fellow-Reauth-Required header on
		// expiry, and X-Fellow-Reauth-Deadline on success) without rendering a page.
		const response = await fetch("/graphql/", {
			method: "POST",
			credentials: "same-origin",
			headers: {
				"Content-Type": "application/json",
				"x-csrftoken": window.CSRF_TOKEN,
				"X-Requested-With": "XMLHttpRequest",
			},
			body: JSON.stringify({ query: SESSION_STATUS_PING_QUERY }),
		});

		if (response.headers.get(REAUTH_REQUIRED_HEADER) === "yes") {
			redirectToLoginForReauth();
			return;
		}

		const deadlineValue = response.headers.get(REAUTH_DEADLINE_HEADER);
		if (deadlineValue) {
			showReauthWarningToast(deadlineValue);
			return;
		}

		dismissReauthWarningToast();
	} catch {
		// Network blip: leave the existing toast and deadline alone. We'll re-probe
		// when the next scheduled timer fires.
	} finally {
		reauthStatusCheckInFlight = false;
	}
}

function scheduleReauthWarningToastUpdate(deadlineValue: string | null): void {
	if (reauthWarningTimeout !== undefined) {
		window.clearTimeout(reauthWarningTimeout);
		reauthWarningTimeout = undefined;
	}

	const deadline = parseDate(deadlineValue);
	if (!deadline) {
		return;
	}

	const remainingMilliseconds = deadline.getTime() - Date.now();
	if (remainingMilliseconds <= 0) {
		checkReauthStatus();
		return;
	}

	const millisecondsUntilLabelChanges = getMillisecondsUntilLabelChanges(remainingMilliseconds);
	reauthWarningTimeout = window.setTimeout(() => {
		if (toast.isActive(REAUTH_TOAST_ID)) {
			const nextRemainingMilliseconds = getRemainingMilliseconds(deadlineValue);
			if (nextRemainingMilliseconds !== null && nextRemainingMilliseconds <= 0) {
				checkReauthStatus();
				return;
			}
			updateReauthWarningToast(deadlineValue);
			scheduleReauthWarningToastUpdate(deadlineValue);
		} else {
			reauthWarningTimeout = undefined;
		}
	}, millisecondsUntilLabelChanges);
}

export function showReauthWarningToast(deadlineValue: string | null): void {
	const remainingMilliseconds = getRemainingMilliseconds(deadlineValue);
	if (remainingMilliseconds === null || remainingMilliseconds > WARNING_DISPLAY_WINDOW_MS) {
		dismissReauthWarningToast();
		return;
	}

	if (toast.isActive(REAUTH_TOAST_ID)) {
		updateReauthWarningToast(deadlineValue);
		scheduleReauthWarningToastUpdate(deadlineValue);
		return;
	}

	showNewReauthWarningToast(deadlineValue);
	scheduleReauthWarningToastUpdate(deadlineValue);
}
